import React from 'react';
import { useParams, Link } from 'react-router-dom';
import './ActivityDetail.css';

const activityDetails = {
  music: {
    title: 'Music',
    tagline: 'Nurturing melody, rhythm and confidence in every child',
    description: [
      'At Pragati Vidyalaya, music is an integral part of everyday school life. From morning assembly songs to special performances, students are encouraged to experience the joy of singing and playing instruments.',
      'Trained music teachers help children explore classical, folk and contemporary styles. Regular practice sessions build vocal strength, sense of rhythm and stage presence.',
      'Students participate in inter-house and inter-school competitions where they learn teamwork, discipline and the importance of regular practice.'
    ],
    highlights: [
      'Dedicated music room with basic instruments for group practice',
      'Special music periods for each class across the week',
      'Opportunities to perform on annual day, national festivals and school events'
    ]
  },
  dance: {
    title: 'Dance',
    tagline: 'Expressing ideas, culture and joy through movement',
    description: [
      'Dance activities at Pragati Vidyalaya help students develop grace, coordination and confidence. Children are introduced to both classical and contemporary dance forms.',
      'Regular practice trains students to express emotions, tell stories and present culture through well-choreographed movements.',
      'Group choreography for events teaches teamwork, listening skills and respect for fellow performers.'
    ],
    highlights: [
      'Practice sessions for classical, folk and freestyle dance',
      'Participation in cultural programmes and competitions',
      'Focus on posture, rhythm, expressions and stage discipline'
    ]
  },
  arts: {
    title: 'Arts',
    tagline: 'Building creativity, observation and imagination',
    description: [
      'Art education at our school encourages children to think creatively and observe the world around them more closely.',
      'Students explore drawing, painting, craft work and basic design activities that suit different age groups.',
      'Every child is motivated to participate, experiment with colours and create artwork with confidence rather than fear of mistakes.'
    ],
    highlights: [
      'Well planned art periods for all classes',
      'Use of different mediums like crayons, water colours and craft material',
      'Display of student artwork in classrooms and school events'
    ]
  },
  sports: {
    title: 'Sports',
    tagline: 'Developing fitness, discipline and team spirit',
    description: [
      'Sports and games are an essential part of education at Pragati Vidyalaya. Regular physical activity keeps students healthy and energetic.',
      'Children are guided to understand rules, fair play and respect for opponents while playing different games.',
      'Through house activities and tournaments, students learn leadership, cooperation and how to handle victory and defeat with grace.'
    ],
    highlights: [
      'Structured physical education periods for all classes',
      'Training in athletics, team games and basic fitness drills',
      'Annual sports day and inter-house competitions'
    ]
  },
  dramatics: {
    title: 'Dramatics',
    tagline: 'Building confidence, expression and language skills',
    description: [
      'Dramatics activities give students a safe platform to express their ideas, emotions and imagination.',
      'Children participate in skits, role plays and small plays based on moral stories, history and everyday life situations.',
      'Practice sessions help them improve voice modulation, pronunciation, body language and timing.'
    ],
    highlights: [
      'Regular class-wise skits and role plays',
      'Opportunities to perform during assemblies and special days',
      'Focus on teamwork, script understanding and clear communication'
    ]
  },
  'public-speaking': {
    title: 'Public Speaking',
    tagline: 'Helping students speak clearly, boldly and respectfully',
    description: [
      'Public speaking is encouraged right from the primary classes so that children feel comfortable sharing their thoughts.',
      'Students participate in speech, elocution, poem recitation and news reading activities during assemblies and class programmes.',
      'Teachers guide them on how to prepare points, organise ideas and speak in front of an audience with confidence.'
    ],
    highlights: [
      'Regular speaking opportunities in class and assembly',
      'Training in voice clarity, eye contact and body posture',
      'Events like speeches, debates and recitations'
    ]
  },
  skating: {
    title: 'Skating',
    tagline: 'Balancing fun, fitness and concentration on wheels',
    description: [
      'Skating sessions introduce students to a fun way of staying fit and active.',
      'Under proper guidance, children learn balancing, basic techniques and safety rules before practising independently.',
      'Regular practice improves focus, coordination and self-confidence as students master new skills.'
    ],
    highlights: [
      'Age appropriate training batches',
      'Safety equipment and instructions followed carefully',
      'Opportunities to participate in demonstrations and events'
    ]
  },
  yoga: {
    title: 'Yoga',
    tagline: 'Strengthening body, mind and values through simple practices',
    description: [
      'Yoga periods help students learn simple asanas, breathing exercises and relaxation techniques suitable for their age.',
      'Through regular practice they develop flexibility, concentration and emotional balance.',
      'Short sessions are also used before exams and important events to help students remain calm and focused.'
    ],
    highlights: [
      'Regular yoga sessions across different classes',
      'Guidance on correct breathing and posture',
      'Emphasis on discipline, inner peace and healthy habits'
    ]
  }
};

const ActivityDetail = () => {
  const { slug } = useParams();
  const activity = activityDetails[slug];

  if (!activity) {
    return (
      <main className="activity-page">
        <div className="activity-container">
          <h1 className="activity-title">Activity Not Found</h1>
          <p className="activity-text">
            The activity you are looking for is not available. Please return to the home page and select a valid activity.
          </p>
          <Link to="/" className="back-link">Back to Home</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="activity-page">
      <div className="activity-container">
        <div className="activity-header">
          <h1 className="activity-title">{activity.title}</h1>
          <p className="activity-tagline">{activity.tagline}</p>
        </div>

        <div className="activity-content">
          <div className="activity-description">
            {activity.description.map((paragraph, index) => (
              <p key={index} className="activity-text">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="activity-highlights">
            <h2 className="activity-subtitle">How the school encourages this activity</h2>
            <ul className="activity-list">
              {activity.highlights.map((item, index) => (
                <li key={index} className="activity-list-item">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="activity-photos-section">
          <h2 className="activity-subtitle">Activity photo gallery</h2>
          <p className="activity-text">
            Photos of {activity.title.toLowerCase()} activities at Pragati Vidyalaya can be added here. You can replace these boxes with actual images whenever they are ready.
          </p>
          <div className="activity-photos-grid">
            <div className="activity-photo-placeholder">Photo 1</div>
            <div className="activity-photo-placeholder">Photo 2</div>
            <div className="activity-photo-placeholder">Photo 3</div>
            <div className="activity-photo-placeholder">Photo 4</div>
          </div>
        </div>

        <div className="activity-back-link-wrapper">
          <Link to="/" className="back-link">Back to Home</Link>
        </div>
      </div>
    </main>
  );
};

export default ActivityDetail;

